import Script from 'next/script';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getCookie } from '@/utility/cookie';
import { isSSR } from '@/utility/isSSR';

const ZENDESK_KEY = process.env.zendeskChatKey;

declare global {
  interface Window {
    zE?: (
      action: string,
      method: string,
      callback?: (cb: (token: string | null) => void) => void,
    ) => void;
  }
}

// Module-level reference to the openChat function
let openChatRef: (() => void) | null = null;

/**
 * Opens the Zendesk chat widget.
 * Call this function from anywhere in your app to trigger the chat.
 */
export const openZendeskChat = () => {
  if (openChatRef) {
    openChatRef();
  }
};

export const ZendeskChat = () => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [hasAuthenticated, setHasAuthenticated] = useState(false);
  const initializingRef = useRef(false);

  // ========================================
  // Authentication Logic
  // ========================================

  const authenticateUser = async (shouldOpen: boolean = false) => {
    if (!window.zE) {
      return;
    }

    window.zE('messenger', 'loginUser', callback => {
      const opts: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      };

      try {
        const sessionCookie = getCookie('outdoorsy-session');
        if (sessionCookie) {
          const session = JSON.parse(sessionCookie);
          const token = session?.authenticated?.token;

          if (token) {
            opts.headers = {
              ...opts.headers,
              Authorization: `Bearer ${token}`,
            };
          }
        }
      } catch {}

      fetch(`${process.env.coreApiHost}/zendesk/sign`, opts)
        .then(resp => {
          if (!resp.ok) return { token: null };
          return resp.json();
        })
        .then(json => {
          callback(json.token);
          if (shouldOpen) {
            setTimeout(() => {
              if (window.zE) {
                window.zE('messenger', 'open');
              }
            }, 0);
          }
        })
        .catch(() => {
          callback(null);

          if (shouldOpen) {
            setTimeout(() => {
              if (window.zE) {
                window.zE('messenger', 'open');
              }
            }, 0);
          }
        });
    });
  };

  // ========================================
  // Chat Trigger & Event Handlers
  // ========================================

  const openChat = useCallback(() => {
    if (!window.zE) {
      return;
    }

    if (!isScriptLoaded) {
      return;
    }

    if (hasAuthenticated) {
      window.zE('messenger', 'open');
    } else {
      setHasAuthenticated(true);
      authenticateUser(true);
    }
  }, [isScriptLoaded, hasAuthenticated]);

  const handleLogin = useCallback(() => {
    if (window.zE && isScriptLoaded) {
      setHasAuthenticated(false);
      authenticateUser(false);
    }
  }, [isScriptLoaded]);

  const handleLogout = useCallback(() => {
    if (window.zE && isScriptLoaded) {
      window.zE('messenger', 'logoutUser');
      setHasAuthenticated(false);
    }
  }, [isScriptLoaded]);

  useEffect(() => {
    // Store reference to openChat at module level so it can be called via openZendeskChat()
    openChatRef = openChat;

    // Listen for authentication events
    window.addEventListener('logout', handleLogout);

    document.addEventListener('authenticate', (event: any) => {
      if (event?.detail?.name === 'logout') {
        handleLogout();
      } else if (event?.detail?.name === 'login') {
        handleLogin();
      }
    });

    // Listen for session 'load-user' event which fires after login/signup
    document.addEventListener('session', (event: any) => {
      if (event?.detail?.name === 'load-user') {
        handleLogin();
      }
    });

    return () => {
      openChatRef = null;
      window.removeEventListener('logout', handleLogout);
    };
  }, [openChat, handleLogin, handleLogout]);

  // ========================================
  // Script Loading
  // ========================================

  const handleScriptLoad = () => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    // Wait for Zendesk to be fully ready
    const checkZendesk = setInterval(() => {
      if (window.zE) {
        clearInterval(checkZendesk);
        setIsScriptLoaded(true);
        initializingRef.current = false;
      }
    }, 100);

    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkZendesk);
      initializingRef.current = false;
    }, 5000);
  };

  // Don't render on server-side
  if (isSSR()) {
    return null;
  }

  // Skip in CI/test environments
  if (typeof window !== 'undefined' && (window as any).__playwright) {
    return null;
  }

  return (
    <Script
      id="ze-snippet"
      src={`https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`}
      strategy="lazyOnload"
      onLoad={handleScriptLoad}
    />
  );
};
